import { Check, Circle, Minus, X } from "lucide-react";
import type { ReactNode } from "react";

import type { SignalCheck } from "../../shared/contracts";
import { cn } from "../lib/utils";
import { Badge } from "./ui/badge";

export function Metric({
  label,
  value,
  detail,
  tone = "neutral",
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: "neutral" | "positive" | "negative";
}) {
  return (
    <div className="min-w-0 border-l border-border pl-4 first:border-l-0 first:pl-0 sm:pl-5">
      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
      <p className={cn(
        "tabular-nums mt-2 font-display text-xl font-bold tracking-[-0.035em] text-foreground [overflow-wrap:anywhere] sm:text-2xl",
        tone === "positive" && "text-positive",
        tone === "negative" && "text-destructive",
      )}>
        {value}
      </p>
      {detail && <p className="mt-1 text-[11px] leading-4 text-muted-foreground [overflow-wrap:anywhere]">{detail}</p>}
    </div>
  );
}

export function DefinitionRow({ label, value, emphasis }: { label: string; value: ReactNode; emphasis?: boolean }) {
  return (
    <div className="flex min-h-10 items-center justify-between gap-4 border-b border-border/75 py-2.5 last:border-b-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className={cn("tabular-nums m-0 text-right text-sm font-semibold text-foreground", emphasis && "text-base")}>{value}</dd>
    </div>
  );
}

const signalConfig = {
  pass: { icon: Check, badge: "positive" as const, label: "Pass" },
  wait: { icon: Minus, badge: "secondary" as const, label: "Waiting" },
  fail: { icon: X, badge: "warning" as const, label: "Blocked" },
};

export function SignalList({ signals }: { signals: SignalCheck[] }) {
  if (signals.length === 0) {
    return <p className="py-6 text-sm text-muted-foreground">Signal checks are not available yet.</p>;
  }
  return (
    <ul className="m-0 grid list-none gap-0 p-0">
      {signals.map((signal) => {
        const config = signalConfig[signal.state];
        const Icon = config.icon;
        return (
          <li key={signal.id} className="flex items-start gap-3 border-b border-border/75 py-3.5 last:border-b-0">
            <span className={cn(
              "mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground",
              signal.state === "pass" && "bg-positive-soft text-positive",
              signal.state === "fail" && "bg-coral-soft text-destructive",
            )}>
              <Icon className="size-3.5" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-foreground">{signal.label}</p>
                <Badge variant={config.badge}>{config.label}</Badge>
              </div>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{signal.detail}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function StatusDot({ active }: { active: boolean }) {
  return <Circle className={cn("size-2 fill-current", active ? "text-positive" : "text-destructive")} aria-hidden="true" />;
}
